import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, Search, ShieldCheck, TimerReset, Upload } from "lucide-react";
import { StaffApiError, staffErrorMessage } from "../client/api";
import type { PublicStoreProfile, UserSession } from "../shared/types";
import { safeReference } from "./referenceDisplay";
import { productBrand } from "../brand";

type ApiClient = <T>(path: string, init?: RequestInit) => Promise<T>;

type ReconciliationCaseView = {
  id: string;
  caseNumber: string;
  provider: string;
  caseType: string;
  status: string;
  severity: string;
  localEntityType?: string;
  localEntityId?: string;
  providerEventId?: string;
  providerPaymentId?: string;
  providerRefundId?: string;
  summary: string;
  updatedAt: string;
};

type InventorySettingsResponse = {
  locations?: { id: string; name: string; code?: string; active?: boolean }[];
  defaults?: { defaultLocationId?: string };
};

type MaintenanceResult = {
  expired: number;
  protected: number;
  skipped: number;
  runId: string;
  preview: boolean;
  outcomes: Array<{ cartId: string; action: "would_expire" | "expired" | "protected" | "skipped"; reason: string }>;
};

type PendingAction = {
  intent: string;
  key: string;
};

type ConfirmAction = { kind: "provider_lookup" | "status" | "expire"; status?: string; preview?: boolean };

const pendingActionKey = "spine_operations_pending_action";
const statusOptions = [
  { value: "investigating", label: "Mark investigating", description: "Records that staff are reviewing persisted evidence." },
  { value: "awaiting_provider", label: "Awaiting provider", description: "Records that provider evidence is still pending or unavailable." },
  { value: "awaiting_local_retry", label: "Awaiting local retry", description: "Records that trusted evidence exists but local recovery still needs review." },
  { value: "escalated", label: "Escalate", description: "Records that the case needs administrator or technical review." },
  { value: "resolved", label: "Resolve with note", description: "Closes the case by staff note only; it does not mark money, inventory, or provider state successful." },
  { value: "dismissed", label: "Dismiss with note", description: "Closes a non-actionable case by staff note only." },
] as const;

function localReference(entry: ReconciliationCaseView): string {
  if (!entry.localEntityType) return "Unmatched";
  return `${entry.localEntityType}: ${safeReference(entry.localEntityId)}`;
}

export function ReconciliationWorkspace({ api, storeProfile, currentUser }: { api: ApiClient; storeProfile: PublicStoreProfile; currentUser: UserSession }) {
  const [cases, setCases] = useState<ReconciliationCaseView[]>([]);
  const [selected, setSelected] = useState<ReconciliationCaseView | null>(null);
  const [locations, setLocations] = useState<InventorySettingsResponse["locations"]>([]);
  const [defaultLocationId, setDefaultLocationId] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [typeFilter, setTypeFilter] = useState("all");
  const [reason, setReason] = useState("");
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [maintenance, setMaintenance] = useState<MaintenanceResult | null>(null);
  const [pending, setPending] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const canOperate = currentUser.role === "administrator" || currentUser.role === "manager";
  const canRunMaintenance = currentUser.role === "administrator";
  const activeLocation = useMemo(() => locations?.find((location) => location.id === defaultLocationId) ?? locations?.[0] ?? null, [defaultLocationId, locations]);

  const filteredCases = useMemo(() => {
    return cases.filter((entry) => {
      if (statusFilter === "active" && ["resolved", "dismissed"].includes(entry.status)) return false;
      if (statusFilter !== "active" && statusFilter !== "all" && entry.status !== statusFilter) return false;
      if (typeFilter !== "all" && entry.caseType !== typeFilter) return false;
      return true;
    });
  }, [cases, statusFilter, typeFilter]);

  const caseTypes = useMemo(() => Array.from(new Set(cases.map((entry) => entry.caseType))).sort(), [cases]);

  const loadContext = useCallback(async () => {
    const [caseData, settingsData] = await Promise.all([
      api<{ cases: ReconciliationCaseView[] }>("/api/admin/reconciliation/cases"),
      api<InventorySettingsResponse>("/api/admin/inventory-settings"),
    ]);
    setCases(caseData.cases);
    setLocations(settingsData.locations ?? []);
    setDefaultLocationId(settingsData.defaults?.defaultLocationId ?? "");
    setSelected((current) => current ? caseData.cases.find((entry) => entry.id === current.id) ?? current : current);
  }, [api]);

  useEffect(() => {
    loadContext().catch((err: unknown) => setError(operationsErrorMessage(err)));
  }, [loadContext]);

  const refreshSelected = async (caseId = selected?.id) => {
    if (!caseId) return;
    const data = await api<{ case: ReconciliationCaseView }>(`/api/admin/reconciliation/cases/${encodeURIComponent(caseId)}`);
    setSelected(data.case);
    setCases((current) => current.map((entry) => entry.id === data.case.id ? data.case : entry));
  };

  const run = async (task: () => Promise<void>) => {
    setError("");
    setMessage("");
    setPending("action");
    try {
      await task();
    } catch (err) {
      setError(operationsErrorMessage(err));
      await refreshSelected().catch(() => undefined);
    } finally {
      setPending("");
      setConfirmAction(null);
    }
  };

  const lookupProvider = async () => {
    if (!selected) return;
    const intent = JSON.stringify({ kind: "provider_lookup", caseId: selected.id, status: selected.status, providerPaymentId: selected.providerPaymentId, providerRefundId: selected.providerRefundId });
    const idempotencyKey = readOrCreatePendingAction(intent);
    const data = await api<{ case: ReconciliationCaseView; providerStatus?: string }>(`/api/admin/reconciliation/cases/${selected.id}/provider-lookup`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey }),
    });
    clearPendingAction();
    setSelected(data.case);
    setMessage(data.providerStatus ? `Trusted provider lookup returned ${data.providerStatus}. Local recovery remains server-authoritative.` : "Provider lookup did not run because this case has no trusted provider payment or refund ID.");
    await loadContext();
  };

  const updateStatus = async (status: string) => {
    if (!selected) return;
    const intent = JSON.stringify({ kind: "status", caseId: selected.id, fromStatus: selected.status, toStatus: status, reason: reason.trim() });
    const idempotencyKey = readOrCreatePendingAction(intent);
    const data = await api<{ case: ReconciliationCaseView }>(`/api/admin/reconciliation/cases/${selected.id}`, {
      method: "POST",
      body: JSON.stringify({ status, reason: reason.trim(), idempotencyKey }),
    });
    clearPendingAction();
    setSelected(data.case);
    setReason("");
    setMessage(`Case ${data.case.caseNumber} is now ${data.case.status}.`);
    await loadContext();
  };

  const expireReservations = async (preview: boolean) => {
    const intent = JSON.stringify({ kind: "reservation_expiration", preview, limit: 25 });
    const idempotencyKey = readOrCreatePendingAction(intent);
    const result = await api<MaintenanceResult>("/api/admin/storefront/maintenance/expire", {
      method: "POST",
      body: JSON.stringify({ limit: 25, preview, idempotencyKey }),
    });
    if (!preview) clearPendingAction();
    setMaintenance(result);
    setMessage(`${result.preview ? "Previewed" : "Ran"} protected expiration batch: ${result.expired} expirable, ${result.protected} protected, ${result.skipped} skipped.`);
    await loadContext();
  };

  const actionDescription = confirmAction ? describeAction(confirmAction, selected) : "";

  return (
    <section className="page operations-workspace">
      <header className="page-header">
        <div><p className="eyebrow">Operations</p><h1>Reconciliation and operational tools</h1></div>
        <button onClick={() => void run(loadContext)} disabled={Boolean(pending)}><RefreshCw /> Refresh</button>
      </header>
      <div className="context-grid">
        <div><span>Product</span><strong>{storeProfile.productName ?? productBrand.name}</strong></div>
        <div><span>Store</span><strong>{storeProfile.storeDisplayName}</strong></div>
        <div><span>Location</span><strong>{activeLocation ? `${activeLocation.name}${activeLocation.code ? ` (${activeLocation.code})` : ""}` : "Backend context required"}</strong></div>
        <div><span>Role</span><strong>{currentUser.role}{canOperate ? " / operations enabled" : " / read only"}</strong></div>
      </div>
      {message && <div className="success" role="status">{message}</div>}
      {error && <div className="error" role="alert">{error}</div>}
      <section className="panel action-panel">
        <div>
          <p className="eyebrow">Inventory files</p>
          <h2>Spreadsheet imports and exports</h2>
          <p>Use the import wizard to upload CSV or XLSX files, map columns, preview inventory changes, export backups, and review import history.</p>
        </div>
        <a className="button-link primary" href="/app/imports"><Upload aria-hidden /> Open import wizard</a>
      </section>
      {!canOperate && (
        <div className="empty-state" role="status">
          <AlertTriangle aria-hidden />
          <h2>Manager review required</h2>
          <p>Operations cases are protected. The backend decides authorization for every read and mutation.</p>
        </div>
      )}

      <div className="workflow-grid">
        <section className="panel">
          <div className="section-title">
            <div><p className="eyebrow">Queue</p><h2>Exception queue</h2></div>
          </div>
          <p>Cases are loaded from persisted reconciliation evidence. Filtering is local over the server-bounded open-case list; no provider lookup runs until explicitly confirmed.</p>
          <div className="form-grid">
            <label>Status filter
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="active">Active cases</option>
                <option value="all">All loaded cases</option>
                <option value="open">Open</option>
                <option value="investigating">Investigating</option>
                <option value="awaiting_provider">Awaiting provider</option>
                <option value="awaiting_local_retry">Awaiting local retry</option>
                <option value="escalated">Escalated</option>
              </select>
            </label>
            <label>Case type
              <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                <option value="all">All case types</option>
                {caseTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
          </div>
          <div className="responsive-table">
            <table>
              <thead><tr><th>Case</th><th>Type</th><th>Status</th><th>Provider</th><th>Local record</th><th>Summary</th><th></th></tr></thead>
              <tbody>
                {filteredCases.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.caseNumber}</td>
                    <td>{entry.caseType}</td>
                    <td><span className={entry.status === "open" ? "status-warn" : "status-ok"}>{entry.status}</span></td>
                    <td>{entry.provider}</td>
                    <td>{localReference(entry)}</td>
                    <td>{entry.summary}</td>
                    <td><button onClick={() => { setSelected(entry); setReason(""); }}><Search /> Inspect</button></td>
                  </tr>
                ))}
                {filteredCases.length === 0 && <tr><td colSpan={7}>No reconciliation cases match the current filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel">
          <div className="section-title">
            <div><p className="eyebrow">Case detail</p><h2>{selected ? selected.caseNumber : "Select a case"}</h2></div>
            {selected && <button onClick={() => void run(() => refreshSelected())} disabled={Boolean(pending)}><RefreshCw /> Check current status</button>}
          </div>
          {!selected ? <p className="empty-inline">Select a case to inspect persisted payment, refund, webhook, reservation, and local evidence.</p> : (
            <>
              <div className="summary-grid">
                <div><span>Type</span><strong>{selected.caseType}</strong></div>
                <div><span>Status</span><strong>{selected.status}</strong></div>
                <div><span>Severity</span><strong>{selected.severity}</strong></div>
                <div><span>Updated</span><strong>{formatDate(selected.updatedAt)}</strong></div>
                <div><span>Local record</span><strong>{localReference(selected)}</strong></div>
                <div><span>Provider event</span><strong>{safeReference(selected.providerEventId) || "None returned"}</strong></div>
                <div><span>Provider payment</span><strong>{safeReference(selected.providerPaymentId) || "None returned"}</strong></div>
                <div><span>Provider refund</span><strong>{safeReference(selected.providerRefundId) || "None returned"}</strong></div>
              </div>
              <p>{selected.summary}</p>
              <div className="warning"><AlertTriangle /> Webhook receipt, provider status, payment state, refund state, inventory state, ledger state, and reservation state remain separate. This screen does not mark money successful by note alone.</div>
              <label>Operational note
                <textarea value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} placeholder="Required for status changes. Do not include secrets or raw provider payloads." />
              </label>
              <div className="actions">
                <button onClick={() => setConfirmAction({ kind: "provider_lookup" })} disabled={!canOperate || Boolean(pending)}><ShieldCheck /> Provider lookup</button>
                {statusOptions.map((option) => (
                  <button key={option.value} onClick={() => setConfirmAction({ kind: "status", status: option.value })} disabled={!canOperate || Boolean(pending) || !reason.trim() || (selected.status === "resolved" && option.value !== "resolved")}>
                    {option.label}
                  </button>
                ))}
              </div>
              <ul className="compact-list">
                {statusOptions.map((option) => <li key={option.value}><strong>{option.label}:</strong> {option.description}</li>)}
              </ul>
            </>
          )}
        </section>
      </div>

      <section className="panel">
        <div className="section-title">
          <div><p className="eyebrow">Maintenance</p><h2>Protected reservation expiration</h2></div>
        </div>
        <p>Preview or run a bounded expiration batch through the existing server contract. Pending, unknown, completed, fulfilled, or reconciliation-protected carts are skipped or protected by the backend.</p>
        {!canRunMaintenance && <p className="status-warn">Administrator authority is required to run reservation maintenance.</p>}
        <div className="actions">
          <button onClick={() => setConfirmAction({ kind: "expire", preview: true })} disabled={!canRunMaintenance || Boolean(pending)}><Search /> Preview expiration batch</button>
          <button onClick={() => setConfirmAction({ kind: "expire", preview: false })} disabled={!canRunMaintenance || Boolean(pending)}><TimerReset /> Run protected expiration batch</button>
        </div>
        {maintenance && (
          <div>
            <div className="summary-grid">
              <div><span>Mode</span><strong>{maintenance.preview ? "Preview" : "Run"}</strong></div>
              <div><span>Run ID</span><strong>{safeReference(maintenance.runId)}</strong></div>
              <div><span>Expirable / expired</span><strong>{maintenance.expired}</strong></div>
              <div><span>Protected</span><strong>{maintenance.protected}</strong></div>
              <div><span>Skipped</span><strong>{maintenance.skipped}</strong></div>
            </div>
            <ul className="compact-list">
              {maintenance.outcomes.slice(0, 25).map((outcome) => (
                <li key={`${outcome.cartId}:${outcome.action}:${outcome.reason}`}><strong>{outcome.action}</strong>: {safeReference(outcome.cartId)} ({outcome.reason})</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="panel">
        <div className="section-title"><div><p className="eyebrow">Boundaries</p><h2>Unsupported recovery controls</h2></div></div>
        <p>This workspace exposes trusted lookup, status notes, and protected reservation expiration only. It does not replay arbitrary webhooks, fabricate provider results, create payment/refund attempts, adjust ledgers, adjust inventory, or mark unresolved records as financially successful.</p>
      </section>

      {confirmAction && (
        <div className="modal-backdrop" role="presentation">
          <section className="modal" role="dialog" aria-modal="true" aria-labelledby="operations-confirm-title">
            <h2 id="operations-confirm-title">Confirm operational action</h2>
            <p>{actionDescription}</p>
            <div className="warning">Ambiguous results will be recovered by reloading the original case or maintenance result. Do not start a replacement operation unless the backend returns a new authoritative state.</div>
            <div className="actions">
              <button onClick={() => setConfirmAction(null)} disabled={Boolean(pending)}>Cancel</button>
              <button className="primary" onClick={() => void run(async () => {
                if (confirmAction.kind === "provider_lookup") await lookupProvider();
                if (confirmAction.kind === "status" && confirmAction.status) await updateStatus(confirmAction.status);
                if (confirmAction.kind === "expire") await expireReservations(Boolean(confirmAction.preview));
              })} disabled={Boolean(pending)}>
                <CheckCircle2 /> Confirm action
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function describeAction(action: ConfirmAction, selected: ReconciliationCaseView | null): string {
  if (action.kind === "provider_lookup") {
    return `Run trusted provider lookup for ${selected?.caseNumber ?? "the selected case"}. This may contact the provider server-side through the existing backend route and route the result through normal reconciliation.`;
  }
  if (action.kind === "status") {
    return `Change ${selected?.caseNumber ?? "the selected case"} to ${action.status}. This records a staff review note only; it does not mark payment, refund, inventory, ledger, webhook, or reservation success.`;
  }
  return action.preview
    ? "Preview the bounded reservation expiration batch. This reads eligibility and does not release inventory."
    : "Run the bounded protected reservation expiration batch. The backend releases only eligible unpaid expired reservations and protects unresolved payment or fulfillment states.";
}

function readOrCreatePendingAction(intent: string): string {
  const existing = safeParsePendingAction(localStorage.getItem(pendingActionKey));
  if (existing?.intent === intent) return existing.key;
  const next: PendingAction = { intent, key: crypto.randomUUID() };
  localStorage.setItem(pendingActionKey, JSON.stringify(next));
  return next.key;
}

function clearPendingAction(): void {
  localStorage.removeItem(pendingActionKey);
}

function safeParsePendingAction(value: string | null): PendingAction | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as PendingAction;
    return parsed && typeof parsed.intent === "string" && typeof parsed.key === "string" ? parsed : null;
  } catch {
    return null;
  }
}

function operationsErrorMessage(error: unknown): string {
  if (error instanceof StaffApiError) return staffErrorMessage(error);
  if (error instanceof Error) return error.message;
  return "The operations state could not be confirmed. Refresh the case before retrying.";
}

function formatDate(value?: string): string {
  if (!value) return "Unknown";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
